import React, { useState, forwardRef, useImperativeHandle, useEffect, useRef } from 'react';
import api from '../../utils/axiosConfig';
import DatePicker from '../../components/DatePicker/DatePicker';

const PartyDetails = forwardRef((props, ref) => {
    const { showClientSelection = false, enableProjectSelection = false, enableCompanySelection = false } = props;
    const [formData, setFormData] = useState({
        invoiceNo: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        partyName: '',
        partyAddress: '',
        siteAddress: '',
        workTitle: '',
        partyGST: '',
        partyPAN: '',
        category: '',
        stateCode: '',
        state: '',
        gstRate: '',
        clientId: '',
        projectId: '',
        companyId: '',
        poNumber: '',
        poDate: new Date().toISOString().split('T')[0],
        vendorCode: ''
    });
    const [showClientModal, setShowClientModal] = useState(false);
    const [clients, setClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [projects, setProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [companies, setCompanies] = useState([]);
    const formDataRef = useRef(formData);
    formDataRef.current = formData;

    const resolveCompanyId = (companyId, prevCompanyId = '') => {
        if (companyId != null && companyId !== '') {
            return String(companyId);
        }
        if (companies.length > 0) {
            return String(companies[0].id);
        }
        return prevCompanyId;
    };

    useEffect(() => {
        if (!enableCompanySelection) return;

        const fetchCompanies = async () => {
            try {
                const response = await api.get('/companies');
                const sorted = [...response.data].sort((a, b) => a.id - b.id);
                setCompanies(sorted);
                setFormData((prev) => {
                    if (prev.companyId) return prev;
                    if (sorted.length > 0) {
                        return { ...prev, companyId: String(sorted[0].id) };
                    }
                    return prev;
                });
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        };

        fetchCompanies();
    }, [enableCompanySelection]);

    const fetchClients = async () => {
        try {
            const response = await api.get('/clients');
            setClients(response.data);
            setFilteredClients(response.data);
        } catch (error) {
            console.error('Error fetching clients:', error);
        }
    };

    const fetchProjectsForClient = async (clientId) => {
        if (!clientId || !enableProjectSelection) {
            setProjects([]);
            return;
        }
        try {
            const response = await api.get('/projects');
            const clientProjects = response.data.filter(
                (project) => String(project.clientId) === String(clientId)
            );
            setProjects(clientProjects);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setProjects([]);
        }
    };

    const handleSearchChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        const filtered = clients.filter(client =>
            (client.name || '').toLowerCase().includes(term.toLowerCase()) ||
            (client.gstNo || '').toLowerCase().includes(term.toLowerCase()) ||
            (client.panNo || '').toLowerCase().includes(term.toLowerCase()) ||
            (client.email || '').toLowerCase().includes(term.toLowerCase()) ||
            (client.phone || '').toLowerCase().includes(term.toLowerCase())
        );
        setFilteredClients(filtered);
    };

    const handleClientSelect = (client) => {
        setSelectedClient(client);
        setFormData(prevData => ({
            ...prevData,
            partyName: client.name,
            partyAddress: client.address || '',
            partyGST: client.gstNo || '',
            partyPAN: client.panNo || '',
            clientId: client.id,
            projectId: '',
            siteAddress: '',
        }));
        fetchProjectsForClient(client.id);
        setShowClientModal(false);
        setSearchTerm('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleProjectChange = (e) => {
        const { value } = e.target;
        const selectedProject = projects.find(
            (project) => String(project.id) === String(value)
        );

        setFormData(prevState => ({
            ...prevState,
            projectId: value,
            siteAddress: selectedProject ? (selectedProject.address || '') : '',
        }));
    };

    useImperativeHandle(ref, () => ({
        getFormData: () => {
            const current = formDataRef.current;
            return {
                ...current,
                clientId: current.clientId || null,
                projectId: current.projectId || null,
                companyId: current.companyId ? parseInt(current.companyId, 10) : null,
                selectedClientId: selectedClient?.id || current.clientId || null,
            };
        },
        setFormData: (data) => {
            setFormData((prev) => ({
                ...prev,
                ...data,
                clientId: data.clientId ? String(data.clientId) : '',
                projectId: data.projectId ? String(data.projectId) : '',
                companyId: resolveCompanyId(data.companyId, prev.companyId),
            }));
            if (data.clientId) {
                setSelectedClient({ id: data.clientId, name: data.partyName });
                fetchProjectsForClient(data.clientId);
            }
        }
    }));

    return (
        <>
            <section className="create-bill-section">
                <div className="create-bill-section-head">
                    <div>
                        <h2>Invoice details</h2>
                        <p className="create-bill-section-hint">Basic invoice and tax information</p>
                    </div>
                </div>

                <div className="create-bill-grid cols-4">
                    <div className="create-bill-field">
                        <label htmlFor="invoiceDate">Invoice date</label>
                        <DatePicker
                            id="invoiceDate"
                            name="invoiceDate"
                            value={formData.invoiceDate}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="gstRate">GST rate (%)</label>
                        <input
                            type="number"
                            id="gstRate"
                            name="gstRate"
                            value={formData.gstRate}
                            onChange={handleChange}
                            placeholder="e.g. 9"
                            required
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="category">Category</label>
                        <input
                            type="text"
                            id="category"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="stateCode">State code</label>
                        <input
                            type="text"
                            id="stateCode"
                            name="stateCode"
                            value={formData.stateCode}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>
            </section>

            {enableCompanySelection && (
                <section className="create-bill-section">
                    <div className="create-bill-section-head">
                        <div>
                            <h2>Company</h2>
                            <p className="create-bill-section-hint">Select the company issuing this invoice</p>
                        </div>
                    </div>

                    <div className="create-bill-grid cols-2">
                        <div className="create-bill-field">
                            <label htmlFor="companyId">Company</label>
                            <select
                                id="companyId"
                                name="companyId"
                                value={formData.companyId}
                                onChange={handleChange}
                                required={companies.length > 0}
                            >
                                {companies.length === 0 ? (
                                    <option value="">No companies available</option>
                                ) : (
                                    companies.map((company) => (
                                        <option key={company.id} value={String(company.id)}>
                                            {company.name}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    </div>
                </section>
            )}

            <section className="create-bill-section">
                <div className="create-bill-section-head">
                    <div>
                        <h2>Client details</h2>
                        <p className="create-bill-section-hint">Who this invoice is billed to</p>
                    </div>
                    {showClientSelection && (
                        <button
                            type="button"
                            className="create-bill-btn-secondary"
                            onClick={() => {
                                fetchClients();
                                setShowClientModal(true);
                            }}
                        >
                            Select client
                        </button>
                    )}
                </div>

                <div className="create-bill-grid">
                    <div className="create-bill-field span-2">
                        <label htmlFor="partyName">Client name</label>
                        <input
                            type="text"
                            id="partyName"
                            name="partyName"
                            value={formData.partyName}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="create-bill-field span-2">
                        <label htmlFor="partyAddress">Client address</label>
                        <input
                            type="text"
                            id="partyAddress"
                            name="partyAddress"
                            value={formData.partyAddress}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="partyGST">GSTIN</label>
                        <input
                            type="text"
                            id="partyGST"
                            name="partyGST"
                            value={formData.partyGST}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="partyPAN">PAN</label>
                        <input
                            type="text"
                            id="partyPAN"
                            name="partyPAN"
                            value={formData.partyPAN}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="state">State</label>
                        <input
                            type="text"
                            id="state"
                            name="state"
                            value={formData.state}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>
            </section>

            <section className="create-bill-section">
                <div className="create-bill-section-head">
                    <div>
                        <h2>Work & site</h2>
                        <p className="create-bill-section-hint">Project title and site location</p>
                    </div>
                </div>

                <div className="create-bill-grid">
                    {enableProjectSelection && (
                        <div className="create-bill-field span-2">
                            <label htmlFor="projectId">Project</label>
                            <select
                                id="projectId"
                                name="projectId"
                                value={formData.projectId}
                                onChange={handleProjectChange}
                                disabled={!formData.clientId}
                            >
                                <option value="">
                                    {formData.clientId ? 'Select a project' : 'Select a client first'}
                                </option>
                                {projects.map((project) => (
                                    <option key={project.id} value={project.id}>
                                        {project.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="create-bill-field span-2">
                        <label htmlFor="workTitle">Work title</label>
                        <input
                            type="text"
                            id="workTitle"
                            name="workTitle"
                            value={formData.workTitle}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className="create-bill-field span-2">
                        <label htmlFor="siteAddress">Site address</label>
                        <input
                            type="text"
                            id="siteAddress"
                            name="siteAddress"
                            value={formData.siteAddress}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>
            </section>

            <section className="create-bill-section">
                <div className="create-bill-section-head">
                    <div>
                        <h2>Purchase order</h2>
                        <p className="create-bill-section-hint">Optional PO and vendor reference</p>
                    </div>
                </div>

                <div className="create-bill-grid cols-3">
                    <div className="create-bill-field">
                        <label htmlFor="poNumber">P.O. number</label>
                        <input
                            type="text"
                            id="poNumber"
                            name="poNumber"
                            value={formData.poNumber}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="poDate">P.O. date</label>
                        <DatePicker
                            id="poDate"
                            name="poDate"
                            value={formData.poDate}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="create-bill-field">
                        <label htmlFor="vendorCode">Vendor code</label>
                        <input
                            type="text"
                            id="vendorCode"
                            name="vendorCode"
                            value={formData.vendorCode}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </section>

            {showClientModal && (
                <div className="create-bill-modal-overlay" onClick={() => setShowClientModal(false)}>
                    <div className="create-bill-modal wide" onClick={(e) => e.stopPropagation()}>
                        <div className="create-bill-modal-head">
                            <div>
                                <h2>Select client</h2>
                                <p>Choose a client to fill name, address, GST, and PAN</p>
                            </div>
                            <button
                                type="button"
                                className="create-bill-btn-ghost"
                                onClick={() => setShowClientModal(false)}
                            >
                                Close
                            </button>
                        </div>

                        <input
                            type="text"
                            placeholder="Search by name, GST, or PAN"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className="create-bill-search"
                            autoFocus
                        />

                        <table className="create-bill-clients-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>GST No</th>
                                    <th>PAN No</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClients.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" style={{ textAlign: 'center', color: '#888' }}>
                                            No clients found
                                        </td>
                                    </tr>
                                ) : filteredClients.map(client => (
                                    <tr key={client.id} onClick={() => handleClientSelect(client)}>
                                        <td>{client.name}</td>
                                        <td>{client.gstNo}</td>
                                        <td>{client.panNo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="create-bill-modal-foot">
                            <button
                                type="button"
                                className="create-bill-btn-secondary"
                                onClick={() => setShowClientModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});

export default PartyDetails;
